import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { parseCookies, setCookie, destroyCookie } from 'nookies';
import { api, API_CONFIG } from '@/utils/api';

// Interface do usuário — reflete o padrão do manhwa-api
export interface User {
    usr_id: number;
    usr_nome: string;
    usr_nick: string;
    usr_imagem?: string;
    usr_email?: string;
    usr_admin?: boolean;
    usr_nivel?: number;
    usr_titulo?: string;
    usr_descricao?: string;
    usr_xp?: number;
    usr_status?: string;
    usr_criado_em?: string;
    is_vip?: boolean;
    vip_acaba_em?: string | null;
    mol_id?: number | null;
    moldura?: { mol_id: number; mol_nome: string; mol_imagem: string; mol_tipo: number } | null;
    [key: string]: any;
}

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (token: string, userData: User, refreshToken?: string) => void;
    logout: () => void;
    /** Limpa sessão, cookies, localStorage, sessionStorage e redireciona para /entrar */
    clearSessionAndCache: () => void;
    updateUser: (updates: Partial<User>) => void;
    /** Atualiza o token via refresh e os dados do usuário. Use quando VIP for ativado (o JWT antigo tem is_vip:false). */
    refreshTokenAndUpdateUser: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const getApiBody = (response: any) => response?.data ?? response;

export function AuthProvider({ children }: { children: ReactNode }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Função para verificar se o token ainda é válido (mesma API do app)
    const verifyToken = useCallback(async (): Promise<boolean> => {
        const cookies = parseCookies();
        const token = cookies.token;
        if (!token) return false;
        try {
            const base = (API_CONFIG.BASE_URL || '').replace(/\/$/, '');
            const response = await fetch(`${base}/usuarios/me`, {
                method: 'GET',
                headers: {
                    'authorization': `Bearer ${token}`,
                    'accept': '*/*',
                    'content-type': 'application/json',
                },
            });
            return response.ok;
        } catch {
            return false;
        }
    }, []);

    const logout = useCallback(() => {
        destroyCookie(null, 'token');
        destroyCookie(null, 'refresh_token');
        if (typeof window !== 'undefined') {
            localStorage.removeItem('userData');
            localStorage.removeItem('authToken');
            // Redirecionamento completo sem setState evita re-renders em cascata e erro React #310 (sessão inválida / login em outro dispositivo)
            window.location.href = `/entrar${window.location.search || ''}`;
            return;
        }
        setUser(null);
        navigate('/entrar');
    }, [navigate]);

    const clearSessionAndCache = useCallback(async () => {
        destroyCookie(null, 'token');
        destroyCookie(null, 'refresh_token');
        if (typeof window !== 'undefined') {
            localStorage.removeItem('userData');
            localStorage.removeItem('authToken');
            localStorage.removeItem('referrerNick');
            localStorage.removeItem('obras_favoritas_cache');
            localStorage.removeItem('notificationsCount');
            localStorage.removeItem('chapter-infinite-scroll');
            localStorage.removeItem('chapter-image-zoom');
            localStorage.removeItem('chapter-image-brightness');
            localStorage.removeItem('chapter-image-saturation');
            localStorage.removeItem('changelog-banner-clicked');
            localStorage.removeItem('savedAccount');
            localStorage.removeItem('rememberedEmail');
            localStorage.removeItem('rememberedPassword');
            sessionStorage.clear();

            // Limpar cache do PWA (Service Worker) para evitar versão antiga
            if ('caches' in window) {
                try {
                    const names = await caches.keys();
                    await Promise.all(names.map((name) => caches.delete(name)));
                } catch (_) { }
            }
            if ('serviceWorker' in navigator) {
                try {
                    const regs = await navigator.serviceWorker.getRegistrations();
                    for (const reg of regs) await reg.unregister();
                } catch (_) { }
            }

            setUser(null);
            const search = window.location.search;
            window.location.href = `/entrar${search}`;
        } else {
            setUser(null);
            navigate('/entrar');
        }
    }, [navigate]);

    // Verificar autenticação ao carregar — à prova de erros (evita "Application error" quando sessão inválida ex.: login em outro dispositivo)
    useEffect(() => {
        let cancelled = false;
        const loadAuth = async () => {
            const cookies = parseCookies();
            const token = cookies.token;

            if (!token) {
                setIsLoading(false);
                return;
            }

            try {
                const userResponse = await api.get('usuarios/me');
                const userData = getApiBody(userResponse);
                if (!cancelled) {
                    setUser(userData);
                }
            } catch (error: any) {
                if (cancelled) return;
                const is401 = error?.isAuthError || error?.status === 401 || error?.statusCode === 401;
                if (is401) {
                    try {
                        const ok = await verifyToken();
                        if (!ok) logout();
                        else if (!cancelled) setIsLoading(false);
                    } catch {
                        logout();
                    }
                } else {
                    setIsLoading(false);
                }
                return;
            }
            if (!cancelled) setIsLoading(false);
        };

        loadAuth();
        return () => { cancelled = true; };
    }, [verifyToken, logout]);

    const login = useCallback((token: string, userData: User, refreshToken?: string) => {
        setCookie(null, 'token', token, {
            maxAge: 60 * 60 * 24 * 7, // 7 dias
            path: '/',
            secure: (import.meta as any).env.PROD,
            sameSite: 'strict',
        });

        if (refreshToken) {
            setCookie(null, 'refresh_token', refreshToken, {
                maxAge: 60 * 60 * 24 * 30, // 30 dias
                path: '/',
                secure: (import.meta as any).env.PROD,
                sameSite: 'strict',
            });
        }

        setUser(userData);

    }, []);

    const updateUser = (updates: Partial<User>) => {
        if (user) {
            const newUser = { ...user, ...updates };
            setUser(newUser);
        }
    };

    const refreshTokenAndUpdateUser = useCallback(async (): Promise<boolean> => {
        const cookies = parseCookies();
        const refreshToken = cookies.refresh_token;
        if (!refreshToken) return false;
        try {
            const refreshResponse = await api.post('auth/refresh', { refresh_token: refreshToken });
            const res = getApiBody(refreshResponse);
            const newToken = res?.access_token ?? res?.token ?? res?.data?.access_token;
            if (!newToken) return false;
            setCookie(null, 'token', newToken, {
                maxAge: 60 * 60 * 24 * 7,
                path: '/',
                secure: (import.meta as any).env.PROD,
                sameSite: 'strict',
            });
            const userResponse = await api.get('usuarios/me');
            const userData = getApiBody(userResponse);
            if (userData) {
                login(newToken, userData);
                return true;
            }
        } catch (e) {
            console.error('[Auth] Erro ao atualizar token:', e);
        }
        return false;
    }, [login]);

    return (
        <AuthContext.Provider value={{
            user,
            isAuthenticated: !!user,
            isLoading,
            login,
            logout,
            clearSessionAndCache,
            updateUser,
            refreshTokenAndUpdateUser
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth deve ser usado dentro de um AuthProvider');
    }
    return context;
}
